import type { Id } from '@hq/utils';
import { toQuery, type HttpClient } from '../client';
import type { PageQuery, PageResult } from '../types/result';

export interface SettleRow {
  id: string;
  bizNo: string;
  orgId: string;
  settlePeriod: string | null;
  settleOrgName: string | null;
  payeeName: string | null;
  settleBasis: string | null;
  settleAmount: number;
  discountAmount: number;
  returnAmount: number;
  taxAmount: number;
  payableAmount: number;
  paidAmount: number;
  settleStatus: string;
  confirmAt: string | null;
  bizDate: string;
  remark: string | null;
}

export interface SettleItemRow {
  id: string;
  settleId: string;
  lineNo: number;
  bizType: string | null;
  bizNo: string | null;
  feeType: string | null;
  deptName: string | null;
  materialCode: string | null;
  materialName: string | null;
  spec: string | null;
  unitName: string | null;
  qty: number;
  price: number;
  amount: number;
  direction: number;
  occurAt: string | null;
}

export interface SettleDetail {
  account: SettleRow;
  items: SettleItemRow[];
}

export interface SettleListQuery extends PageQuery {
  settleStatus?: string;
  settlePeriod?: string;
  /** 收款方视角：只返回 `payee_supplier_id` = 当前登录机构 的对账单（供应商门户用）。 */
  payeeOnly?: boolean;
}

export interface BudgetUsageRow {
  id: string;
  deptId: string;
  deptName: string | null;
  budgetYear: number;
  periodNo: number;
  bizType: string | null;
  bizNo: string | null;
  usageType: string | null;
  amount: number;
  beforeUsed: number;
  afterUsed: number;
  execRateSnapshot: number;
  overBudgetFlag: boolean;
  bizDate: string | null;
  remark: string | null;
}

export interface BudgetUsageQuery extends PageQuery {
  deptId?: string;
  budgetYear?: number;
  overBudgetOnly?: boolean;
}

export interface BudgetBalanceQuery {
  /** 科室口径；不传 = 全机构。 */
  deptId?: string;
  /** 预算年度；缺省当前年。 */
  budgetYear?: number;
}

export interface BudgetBalanceDeptRow {
  deptId: string;
  deptName: string | null;
  budgetAmount: number;
  usedAmount: number;
  frozenAmount: number;
  availableAmount: number;
  execRate: number;
  overBudget: boolean;
}

export interface BudgetBalanceResult {
  orgId: string;
  deptId: string;
  budgetYear: number;
  budgetAmount: number;
  usedAmount: number;
  frozenAmount: number;
  /** 额度 - 已用 - 冻结（可为负）。 */
  availableAmount: number;
  execRate: number;
  overBudget: boolean;
  depts: BudgetBalanceDeptRow[];
  note: string;
}

// ---------------- 预算编制（P8） ----------------

export type BudgetPlanStatus = 'DRAFT' | 'SUBMITTED' | 'APPROVING' | 'APPROVED' | 'RETURNED' | 'REJECTED' | 'RELEASED';
export type BudgetPlanType = 'ANNUAL' | 'ADJUST';

export interface BudgetPlanQuery extends PageQuery {
  budgetYear?: number;
  status?: BudgetPlanStatus;
  planType?: BudgetPlanType;
}

export interface BudgetPlanRow {
  id: Id;
  bizNo: string;
  orgId: Id;
  orgName: string | null;
  submitOrgId: Id;
  approveOrgId: Id;
  budgetYear: number;
  planVersion: number;
  planType: BudgetPlanType | string;
  totalAmount: number;
  approvedAmount: number;
  status: BudgetPlanStatus | string;
  instanceId: Id;
  submitAt: string | null;
  approveAt: string | null;
  rejectReason: string | null;
  cfgBudgetId: Id;
  bizDate: string;
  remark: string | null;
  itemCount: number;
}

export interface BudgetPlanItemRow {
  id: Id;
  planId: Id;
  lineNo: number;
  deptId: Id;
  deptName: string | null;
  categoryId: Id;
  categoryName: string | null;
  materialId: Id;
  materialName: string | null;
  lastYearAmount: number;
  applyAmount: number;
  approvedAmount: number;
  calcBasis: string | null;
}

export interface BudgetPlanItemInput {
  deptId: Id;
  deptName?: string;
  categoryId: Id;
  categoryName?: string;
  materialId: Id;
  materialName?: string;
  lastYearAmount?: number;
  applyAmount: number;
  approvedAmount?: number;
  calcBasis?: string;
}

export interface BudgetPlanSaveRequest {
  budgetYear: number;
  planType?: BudgetPlanType;
  approveOrgId?: Id;
  remark?: string;
  items: BudgetPlanItemInput[];
}

export interface BudgetPlanApproveRequest {
  /** 批复总额；缺省 = 申报总额（逐条等比缩放）。 */
  approvedAmount?: number;
  opinion?: string;
}

export interface BudgetPlanDetail {
  plan: BudgetPlanRow;
  items: BudgetPlanItemRow[];
}

export interface SettleGenerateRequest {
  /** 期间 yyyy-MM，缺省当月。 */
  period?: string;
  supplierId: string;
  supplierName?: string;
  /**
   * 结算依据（后端 SettleActions.GenerateAsync）：
   * - `SIGNED`（默认）按已签收配送明细汇总
   * - `CONSUMED` 按科室消耗确认（wl_cost_transfer）汇总，适用于零库存
   */
  basis?: 'SIGNED' | 'CONSUMED';
}

export interface SettleGenerateResult {
  id: string;
  bizNo: string;
  period: string;
  settleAmount: number;
  itemCount: number;
  /** 后端同时返回的明细口径字段。 */
  goodsAmount?: number;
  feeAmount?: number;
  feeItemCount?: number;
  settledApplyCount?: number;
  dueDate?: string | null;
}

export interface SettleInvoiceRequest {
  invoiceNo: string;
  invoiceCode?: string;
  invoiceType?: string;
  /** 发票**价税合计**（含税）；服务端按「累计价税合计 ≤ 应付含税」校验并拆分不含税金额。 */
  amount: number;
  /** 价税合计中的税额（不能大于 amount）。 */
  taxAmount?: number;
  invoiceAt?: string;
  fileUrl?: string;
  remark?: string;
}

export interface SettlePaymentRequest {
  payAmount: number;
  payMethod?: string;
  payAt?: string;
  voucherNo?: string;
  voucherUrl?: string;
  remark?: string;
}

export interface InvoiceRow {
  id: string;
  orgId: string;
  invoiceNo: string;
  invoiceCode: string | null;
  invoiceType: string | null;
  settleId: string;
  sellerSupplierId: string;
  sellerName: string | null;
  amount: number;
  taxAmount: number;
  totalAmount: number;
  invoiceAt: string | null;
  fileUrl: string | null;
  status: string | null;
  remark: string | null;
}

export interface PaymentRow {
  id: string;
  orgId: string;
  payNo: string;
  settleId: string;
  payDirection: string | null;
  payAmount: number;
  payMethod: string | null;
  payAt: string | null;
  voucherNo: string | null;
  voucherUrl: string | null;
  status: string | null;
  remark: string | null;
}

export interface PaymentResult {
  paymentId: string;
  payNo: string;
  settleId: string;
  paidAmount: number;
  status: string;
}

/** 争议登记（POST /wl/settle/{id}/dispute）。 */
export interface SettleDisputeRequest {
  reason?: string;
  /** 争议金额，用于调账参考；不传则按整单争议。 */
  disputeAmount?: number;
  /** 证据文件 id，逗号分隔（Hq.Res.Api 上传后返回）。 */
  evidenceIds?: string;
}

/** 争议裁定（POST /wl/settle/{id}/arbitrate）。 */
export interface SettleArbitrateRequest {
  /** ADJUST 调账 / REOPEN 重开 / WRITE_OFF 核销 / CLOSE 关闭 */
  action: 'ADJUST' | 'REOPEN' | 'WRITE_OFF' | 'CLOSE';
  /** ADJUST 时必填：调账金额（可负）+ 应付金额与折让金额同步累加。 */
  adjustAmount?: number;
  opinion?: string;
  evidenceIds?: string;
}

export interface SettleArbitrateResult {
  id: string;
  /** ADJUST→RE_ADJUSTED / REOPEN→GENERATED / WRITE_OFF→WRITTEN_OFF / CLOSE→CLOSED */
  status: string;
  adjustAmount: number;
  payableAmount: number;
  evidenceIds?: string | null;
}

/** 账龄行（GET /wl/settle/aging）。 */
export interface SettleAgingRow {
  id: string;
  bizNo: string;
  orgId: string;
  settlePeriod: string;
  payeeSupplierId: string;
  payeeName: string | null;
  payableAmount: number;
  paidAmount: number;
  outstanding: number;
  dueDate: string | null;
  overdueDays: number | null;
  /** NOT_DUE / D0_30 / D31_60 / D61_90 / D90_PLUS */
  agingBucket: string;
  settleStatus: string;
}

export interface SettleAgingResult {
  totalOutstanding: number;
  /** 库表是否存在 due_date 列；false 时到期日按「账期结束 + 60 天」推算。 */
  hasDueDateColumn: boolean;
  buckets: { bucket: string; count: number; amount: number }[];
  items: SettleAgingRow[];
}

/** 服务费规则（`cfg_service_fee`）：可配置费率、可设 0、免收仍出 0 元行留痕。 */
export interface ServiceFeeRow {
  id: string;
  ruleCode: string;
  ruleName: string;
  orgId: string;
  supplierId: string;
  supplierName: string | null;
  feeType: string;
  chargeMode: string;
  rate: number;
  unitPrice: number;
  fixedAmount: number;
  minAmount: number;
  maxAmount: number;
  freeFlag: boolean;
  taxRate: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  contractId: string;
  status: string;
  remark: string | null;
  updateAt: string | null;
}

export interface ServiceFeeQuery {
  keyword?: string;
  feeType?: string;
  status?: string;
}

export interface ServiceFeeSaveRequest {
  id?: Id;
  ruleCode: string;
  ruleName: string;
  orgId?: Id;
  supplierId?: Id;
  feeType: string;
  chargeMode: string;
  rate?: number;
  unitPrice?: number;
  fixedAmount?: number;
  minAmount?: number;
  maxAmount?: number;
  freeFlag?: boolean;
  taxRate?: number;
  effectiveFrom?: string;
  effectiveTo?: string | null;
  contractId?: Id;
  status?: string;
  remark?: string | null;
}

/** 结算对账单、对账动作与预算使用流水。 */
export function createBillingApi(http: HttpClient) {
  return {
    settle: {
      list(query: SettleListQuery = {}): Promise<PageResult<SettleRow>> {
        return http.request<PageResult<SettleRow>>({ path: '/wl/settle', query: toQuery(query) });
      },

      /** `payeeOnly` 为供应商视角（按收款方校验归属）。 */
      detail(id: Id, options: { payeeOnly?: boolean } = {}): Promise<SettleDetail> {
        return http.request<SettleDetail>({ path: `/wl/settle/${id}`, query: toQuery(options) });
      },

      /** 按期间 + 供应商汇总已签收配送，生成对账单（DRAFT）。 */
      generate(body: SettleGenerateRequest): Promise<SettleGenerateResult> {
        return http.request<SettleGenerateResult>({ path: '/wl/settle/generate', method: 'POST', body });
      },

      /** 对账单确认（DRAFT/GENERATED/SENT → CONFIRMED）。 */
      confirm(id: Id): Promise<{ id: string; status: string }> {
        return http.request<{ id: string; status: string }>({ path: `/wl/settle/${id}/confirm`, method: 'POST' });
      },

      /** 登记发票并置 INVOICED。 */
      invoice(id: Id, body: SettleInvoiceRequest): Promise<{ invoiceId: string; settleId: string; status: string }> {
        return http.request<{ invoiceId: string; settleId: string; status: string }>({
          path: `/wl/settle/${id}/invoice`,
          method: 'POST',
          body
        });
      },

      /** 登记付款并累加已付金额。 */
      payment(id: Id, body: SettlePaymentRequest): Promise<PaymentResult> {
        return http.request<PaymentResult>({ path: `/wl/settle/${id}/payment`, method: 'POST', body });
      },

      invoices(settleId?: Id): Promise<InvoiceRow[]> {
        return http.request<InvoiceRow[]>({ path: '/wl/invoice', query: toQuery({ settleId }) });
      },

      payments(settleId?: Id): Promise<PaymentRow[]> {
        return http.request<PaymentRow[]>({ path: '/wl/payment', query: toQuery({ settleId }) });
      },

      /** 登记争议（DRAFT…RE_ADJUSTED → DISPUTED）。 */
      dispute(id: Id, body: SettleDisputeRequest): Promise<{ id: string; status: string; evidenceIds?: string | null }> {
        return http.request({ path: `/wl/settle/${id}/dispute`, method: 'POST', body });
      },

      /** 争议裁定：ADJUST 调账 / REOPEN 重开 / WRITE_OFF 核销 / CLOSE 关闭。 */
      arbitrate(id: Id, body: SettleArbitrateRequest): Promise<SettleArbitrateResult> {
        return http.request<SettleArbitrateResult>({ path: `/wl/settle/${id}/arbitrate`, method: 'POST', body });
      },

      /** 账龄：按到期日分桶（NOT_DUE / D0_30 / D31_60 / D61_90 / D90_PLUS）。 */
      aging(query: { supplierId?: Id; size?: number } = {}): Promise<SettleAgingResult> {
        return http.request<SettleAgingResult>({ path: '/wl/settle/aging', query: toQuery(query) });
      }
    },

    budget: {
      usage(query: BudgetUsageQuery = {}): Promise<PageResult<BudgetUsageRow>> {
        return http.request<PageResult<BudgetUsageRow>>({ path: '/wl/budget/usage', query: toQuery(query) });
      },
      /** 预算余额（额度/已用/冻结/可用/执行率，可按科室下钻）。 */
      balance(query: BudgetBalanceQuery = {}): Promise<BudgetBalanceResult> {
        return http.request<BudgetBalanceResult>({ path: '/wl/budget/balance', query: toQuery(query) });
      },

      /**
       * 预算编制（`wl_budget_plan` + `_item`，P8 自下而上填报）。
       * 状态机：DRAFT → SUBMITTED/APPROVING → APPROVED → 下达；退回 RETURNED 可再编辑。
       */
      plans: {
        list(query: BudgetPlanQuery = {}): Promise<PageResult<BudgetPlanRow>> {
          return http.request<PageResult<BudgetPlanRow>>({ path: '/wl/budget/plan', query: toQuery(query) });
        },

        detail(id: Id): Promise<BudgetPlanDetail> {
          return http.request<BudgetPlanDetail>({ path: `/wl/budget/plan/${id}` });
        },

        create(body: BudgetPlanSaveRequest): Promise<{ id: Id; bizNo: string; status: string }> {
          return http.request<{ id: Id; bizNo: string; status: string }>({
            path: '/wl/budget/plan',
            method: 'POST',
            body
          });
        },

        update(id: Id, body: BudgetPlanSaveRequest): Promise<{ id: Id; status: string }> {
          return http.request<{ id: Id; status: string }>({
            path: `/wl/budget/plan/${id}`,
            method: 'PUT',
            body
          });
        },

        submit(id: Id): Promise<{ id: Id; status: string }> {
          return http.request<{ id: Id; status: string }>({
            path: `/wl/budget/plan/${id}/submit`,
            method: 'POST',
            body: {}
          });
        },

        /** 批复：`approvedAmount` 缺省按申报总额全额通过，逐条按比例等比缩放。 */
        approve(id: Id, body: BudgetPlanApproveRequest = {}): Promise<{ id: Id; status: string; approvedAmount: number }> {
          return http.request<{ id: Id; status: string; approvedAmount: number }>({
            path: `/wl/budget/plan/${id}/approve`,
            method: 'POST',
            body
          });
        },

        reject(id: Id, body: BudgetPlanApproveRequest = {}): Promise<{ id: Id; status: string }> {
          return http.request<{ id: Id; status: string }>({
            path: `/wl/budget/plan/${id}/reject`,
            method: 'POST',
            body
          });
        },

        /** 下达：把批复金额写成正式预算（`cfg_budget`，org+年度唯一，重复下达覆盖）。 */
        release(id: Id): Promise<{ id: Id; status: string; budgetId: Id }> {
          return http.request<{ id: Id; status: string; budgetId: Id }>({
            path: `/wl/budget/plan/${id}/release`,
            method: 'POST',
            body: {}
          });
        },

        remove(id: Id): Promise<{ id: Id; deleted: boolean }> {
          return http.request<{ id: Id; deleted: boolean }>({
            path: `/wl/budget/plan/${id}`,
            method: 'DELETE'
          });
        }
      }
    },

    /** 服务费规则维护（运营端菜单「服务费规则」）。 */
    serviceFee: {
      list(query: ServiceFeeQuery = {}): Promise<ServiceFeeRow[]> {
        return http.request<ServiceFeeRow[]>({ path: '/wl/service-fee', query: toQuery(query) });
      },

      save(body: ServiceFeeSaveRequest): Promise<{ id: Id; created?: boolean; updated?: boolean }> {
        return http.request({ path: '/wl/service-fee', method: 'POST', body });
      },

      remove(id: Id): Promise<{ id: Id; deleted: boolean }> {
        return http.request<{ id: Id; deleted: boolean }>({ path: `/wl/service-fee/${id}`, method: 'DELETE' });
      }
    }
  };
}

export type BillingApi = ReturnType<typeof createBillingApi>;
